import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { useUser } from "@clerk/clerk-react";

const DOCUMENT_WRITER_THREAD_ID_KEY = "manageclean-document-writer-thread-id";
const DOCUMENT_WRITER_DOCUMENT_ID_KEY = "manageclean-document-writer-document-id";
const DOCUMENT_WRITER_SIDEBAR_VISIBLE_KEY = "manageclean-document-writer-sidebar-visible";

interface DocumentWriterContextType {
  // Thread state
  threadId: string | null;
  setThreadId: (threadId: string | null) => void;
  clearThread: () => void;
  forceRefreshThread: () => void;

  // Document state (specific to Document Writer)
  activeDocumentId: string | null;
  setActiveDocument: (documentId: string | null) => void;

  // Sidebar visibility
  isSidebarVisible: boolean;
  showSidebar: () => void;
  hideSidebar: () => void;
  toggleSidebar: () => void;

  // Canvas scroll control - allows sidebar to trigger scroll to top on refinement
  registerScrollCallback: (callback: () => void) => void;
  unregisterScrollCallback: () => void;
  triggerCanvasScroll: () => void;
}

const DocumentWriterContext = createContext<DocumentWriterContextType | null>(null);

export function useDocumentWriter() {
  const context = useContext(DocumentWriterContext);
  if (!context) {
    throw new Error("useDocumentWriter must be used within a DocumentWriterProvider");
  }
  return context;
}

interface DocumentWriterProviderProps {
  children: ReactNode;
}

export function DocumentWriterProvider({ children }: DocumentWriterProviderProps) {
  const { user, isLoaded } = useUser();
  // Persist threadId across component remounts and page reloads
  const [threadId, setThreadIdState] = useState<string | null>(null);

  // Load user-specific threadId when user loads
  useEffect(() => {
    if (isLoaded) {
      if (user) {
        const key = `${DOCUMENT_WRITER_THREAD_ID_KEY}:${user.id}`;
        const stored = localStorage.getItem(key);
        setThreadIdState(stored || null);
      } else {
        // Clear state on logout
        setThreadIdState(null);
      }
    }
  }, [user, isLoaded]);

  // Persist active document ID
  const [activeDocumentId, setActiveDocumentIdState] = useState<string | null>(null);

  // Load user-specific documentId when user loads
  useEffect(() => {
    if (isLoaded) {
      if (user) {
        const key = `${DOCUMENT_WRITER_DOCUMENT_ID_KEY}:${user.id}`;
        const stored = localStorage.getItem(key);
        setActiveDocumentIdState(stored || null);
      } else {
        // Clear state on logout
        setActiveDocumentIdState(null);
      }
    }
  }, [user, isLoaded]);

  // Persist sidebar visibility
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(DOCUMENT_WRITER_SIDEBAR_VISIBLE_KEY);
      // Default to true if not set
      return stored === null ? true : stored === "true";
    } catch (error) {
      console.error("[DocumentWriter] Failed to load sidebar visibility from localStorage:", error);
      return true;
    }
  });

  // Persist threadId to localStorage whenever it changes
  useEffect(() => {
    if (!isLoaded || !user) return;
    try {
      const key = `${DOCUMENT_WRITER_THREAD_ID_KEY}:${user.id}`;
      if (threadId) {
        localStorage.setItem(key, threadId);
        console.log("[DocumentWriter] Saved threadId to localStorage:", threadId);
      } else {
        localStorage.removeItem(key);
        console.log("[DocumentWriter] Removed threadId from localStorage");
      }
    } catch (error) {
      console.error("[DocumentWriter] Failed to save threadId to localStorage:", error);
    }
  }, [threadId, user, isLoaded]);

  // Persist active document ID to localStorage
  useEffect(() => {
    if (!isLoaded || !user) return;
    try {
      const key = `${DOCUMENT_WRITER_DOCUMENT_ID_KEY}:${user.id}`;
      if (activeDocumentId) {
        localStorage.setItem(key, activeDocumentId);
        console.log("[DocumentWriter] Saved documentId to localStorage:", activeDocumentId);
      } else {
        localStorage.removeItem(key);
        console.log("[DocumentWriter] Removed documentId from localStorage");
      }
    } catch (error) {
      console.error("[DocumentWriter] Failed to save documentId to localStorage:", error);
    }
  }, [activeDocumentId, user, isLoaded]);

  // Persist sidebar visibility to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DOCUMENT_WRITER_SIDEBAR_VISIBLE_KEY, String(isSidebarVisible));
    } catch (error) {
      console.error("[DocumentWriter] Failed to save sidebar visibility to localStorage:", error);
    }
  }, [isSidebarVisible]);

  const setThreadId = (newThreadId: string | null) => {
    console.log("[DocumentWriter] Setting threadId:", newThreadId);
    setThreadIdState(newThreadId);
  };

  const clearThread = () => {
    console.log("[DocumentWriter] Clearing thread");
    setThreadIdState(null);
    setActiveDocumentIdState(null);
    if (isLoaded && user) {
      localStorage.removeItem(`${DOCUMENT_WRITER_THREAD_ID_KEY}:${user.id}`);
      localStorage.removeItem(`${DOCUMENT_WRITER_DOCUMENT_ID_KEY}:${user.id}`);
    }
  };

  const forceRefreshThread = () => {
    console.log("[DocumentWriter] Forcing thread refresh");
    setThreadIdState(null);
    if (isLoaded && user) {
      localStorage.removeItem(`${DOCUMENT_WRITER_THREAD_ID_KEY}:${user.id}`);
    }
  };

  const setActiveDocument = (documentId: string | null) => {
    console.log("[DocumentWriter] Setting active document:", documentId);
    setActiveDocumentIdState(documentId);
  };

  const showSidebar = () => {
    console.log("[DocumentWriter] Showing sidebar");
    setIsSidebarVisible(true);
  };

  const hideSidebar = () => {
    console.log("[DocumentWriter] Hiding sidebar");
    setIsSidebarVisible(false);
  };

  const toggleSidebar = () => {
    console.log("[DocumentWriter] Toggling sidebar");
    setIsSidebarVisible((prev) => !prev);
  };

  // Scroll callback for canvas - registered by the page, triggered by the sidebar
  const scrollCallbackRef = useRef<(() => void) | null>(null);

  const registerScrollCallback = useCallback((callback: () => void) => {
    scrollCallbackRef.current = callback;
  }, []);

  const unregisterScrollCallback = useCallback(() => {
    scrollCallbackRef.current = null;
  }, []);

  const triggerCanvasScroll = useCallback(() => {
    if (scrollCallbackRef.current) {
      scrollCallbackRef.current();
    }
  }, []);

  const contextValue: DocumentWriterContextType = {
    threadId,
    setThreadId,
    clearThread,
    forceRefreshThread,
    activeDocumentId,
    setActiveDocument,
    isSidebarVisible,
    showSidebar,
    hideSidebar,
    toggleSidebar,
    registerScrollCallback,
    unregisterScrollCallback,
    triggerCanvasScroll,
  };

  return (
    <DocumentWriterContext.Provider value={contextValue}>{children}</DocumentWriterContext.Provider>
  );
}
